var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useCallback } from 'react';
import { Box, ListItem, ListItemText, Chip, Button, Typography, useTheme, useMediaQuery } from '@mui/material';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useAuth } from '../../contexts/AuthContext';
import { UserService } from '../../services/UserService';
import { isRestApiMode, apiClient } from '../../services/restApi';
const GoogleCalendarConnector = ({ description, userType = 'admin' }) => {
    const [loading, setLoading] = useState(false);
    const [isGoogleCalendarConnected, setIsGoogleCalendarConnected] = useState(false);
    const functions = getFunctions(undefined, 'europe-west3');
    const { user } = useAuth();
    const userService = UserService.getInstance();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const checkGoogleCalendarConnection = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        if (!user)
            return;
        setLoading(true);
        try {
            const userData = yield userService.getUserData(user.uid);
            setIsGoogleCalendarConnected((userData === null || userData === void 0 ? void 0 : userData.googleCalendarConnected) || false);
        }
        catch (error) {
            console.error('Error checking Google Calendar connection:', error);
        }
        finally {
            setLoading(false);
        }
    }), [user, userService, userType]);
    useEffect(() => {
        checkGoogleCalendarConnection();
    }, [checkGoogleCalendarConnection]);
    const handleConnectGoogleCalendar = () => __awaiter(void 0, void 0, void 0, function* () {
        if (!user)
            return;
        setLoading(true);
        try {
            const baseUrl = `${window.location.protocol}//${window.location.host}`;
            // Сохраняем текущий URL для возврата после авторизации
            const returnUrl = window.location.href;
            localStorage.setItem('googleAuthReturnUrl', returnUrl);
            let authUrl;
            if (isRestApiMode()) {
                const data = yield apiClient.post('/v1/google-calendar/auth', {
                    entityId: user.uid,
                    entityType: userType === 'admin' ? 'admin_users' : 'users',
                    redirectUri: `${baseUrl}/oauth2callback`,
                });
                authUrl = data.authUrl;
            }
            else {
                const initiateGoogleAuth = httpsCallable(functions, 'initiateGoogleAuth');
                const result = yield initiateGoogleAuth({
                    entityId: user.uid,
                    entityType: userType === 'admin' ? 'admin_users' : 'users',
                    redirectUri: `${baseUrl}/oauth2callback`,
                });
                authUrl = result.data.authUrl;
            }
            // Вместо открытия в попапе, делаем редирект
            window.location.href = authUrl;
        }
        catch (error) {
            console.error('Error initiating Google Calendar auth:', error);
            setLoading(false);
        }
    });
    useEffect(() => {
        // Проверяем, вернулись ли мы после авторизации
        const params = new URLSearchParams(window.location.search);
        const status = params.get('google_auth_status');
        if (status === 'success') {
            checkGoogleCalendarConnection();
            // Очищаем параметры URL
            const newUrl = window.location.pathname;
            window.history.replaceState({}, '', newUrl);
            // Возвращаемся на исходную страницу, если она была сохранена
            const returnUrl = localStorage.getItem('googleAuthReturnUrl');
            if (returnUrl && returnUrl !== window.location.href) {
                localStorage.removeItem('googleAuthReturnUrl');
                window.location.href = returnUrl;
            }
        }
    }, [checkGoogleCalendarConnection]);
    return (_jsx(ListItem, { children: _jsxs(Box, Object.assign({ sx: { width: '100%' } }, { children: [_jsxs(Box, Object.assign({ sx: {
                        display: 'flex',
                        flexDirection: isMobile ? 'column' : 'row',
                        justifyContent: 'space-between',
                        alignItems: isMobile ? 'flex-start' : 'center',
                        mb: 1
                    } }, { children: [_jsx(ListItemText, { primary: _jsxs(Box, Object.assign({ sx: { display: 'flex', alignItems: 'center', mb: isMobile ? 1 : 0 } }, { children: ["Google Calendar", isGoogleCalendarConnected && (_jsx(Chip, { label: "\u041F\u043E\u0434\u043A\u043B\u044E\u0447\u0435\u043D\u043E", color: "success", size: "small", sx: { ml: 1 } }))] })), secondary: loading
                                ? "Проверка соединения..."
                                : (isGoogleCalendarConnected
                                    ? "Подключено"
                                    : "Не подключено") }), isGoogleCalendarConnected ? (_jsx(Button, Object.assign({ onClick: handleConnectGoogleCalendar, disabled: loading || !user, size: "small", variant: "outlined", sx: { mt: isMobile ? 1 : 0 } }, { children: "\u041F\u0435\u0440\u0435\u043F\u043E\u0434\u043A\u043B\u044E\u0447\u0438\u0442\u044C" }))) : (_jsx(Button, Object.assign({ onClick: handleConnectGoogleCalendar, disabled: loading || !user, variant: "contained", color: "primary", sx: { mt: isMobile ? 1 : 0 } }, { children: "\u041F\u043E\u0434\u043A\u043B\u044E\u0447\u0438\u0442\u044C" })))] })), _jsx(Typography, Object.assign({ variant: "body2", color: "text.secondary" }, { children: description }))] })) }));
};
export default GoogleCalendarConnector;
